/**
 * LegalPage — shared shell used by /privacidad and /terminos.
 *
 * Mirrors the landing header (logo + lang switch) and footer so legal
 * routes don't feel like an island. Renders structured `Section`s for
 * SEO + readability (h2 anchors).
 *
 * The actual legal copy is owned by `LegalPrivacy.jsx` and `LegalTerms.jsx`
 * — this file just lays out the page chrome.
 *
 * IMPORTANT: The copy is a starting template adapted to Lumfi's data
 * model (no bank scraping, AI-assisted log keeping, multi-LATAM).
 * Have a local lawyer review before going to production with binding
 * obligations.
 */
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../../contexts/LangContext";
import { LumfiWordmark } from "../../components/LumfiLogo";

const FOOTER_COPY = {
  es: {
    tagline: "El piloto de flujo de caja para freelancers.",
    rights: "Todos los derechos reservados",
    legal: "Legal",
    product: "Producto",
    privacy: "Política de privacidad",
    terms: "Términos y condiciones",
    pricing: "Precios",
    features: "Funcionalidades",
    home: "Volver al inicio",
    last_updated: "Última actualización",
  },
  en: {
    tagline: "The cash-flow pilot for freelancers.",
    rights: "All rights reserved",
    legal: "Legal",
    product: "Product",
    privacy: "Privacy policy",
    terms: "Terms and conditions",
    pricing: "Pricing",
    features: "Features",
    home: "Back to home",
    last_updated: "Last updated",
  },
  pt: {
    tagline: "O piloto de fluxo de caixa para freelancers.",
    rights: "Todos os direitos reservados",
    legal: "Legal",
    product: "Produto",
    privacy: "Política de privacidade",
    terms: "Termos e condições",
    pricing: "Preços",
    features: "Funcionalidades",
    home: "Voltar para a home",
    last_updated: "Última atualização",
  },
};

export function LegalPage({ title, eyebrow, lastUpdated, children, dataTestid }) {
  const { lang, setLang } = useLang();
  const t = FOOTER_COPY[lang] || FOOTER_COPY.es;
  const { pathname } = useLocation();

  // The URL slug telegraphs the intended language: /privacy + /terms
  // → English, /privacidad + /terminos → Spanish. Force the locale on
  // first paint so a user who shared the EN link with a friend whose
  // localStorage cached `lang=es` still sees English copy.
  useEffect(() => {
    const enSlugs = ["/privacy", "/terms"];
    const esSlugs = ["/privacidad", "/terminos"];
    if (enSlugs.includes(pathname) && lang !== "en") setLang("en");
    else if (esSlugs.includes(pathname) && lang !== "es" && lang !== "pt") setLang("es");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div className="min-h-screen bg-cream-50" data-testid={dataTestid}>
      {/* Header — minimal: logo left, lang switcher right. */}
      <header className="sticky top-0 z-30 bg-cream-50/85 backdrop-blur border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-back-home">
            <LumfiWordmark className="h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-1 text-xs">
            {["es", "en", "pt"].map((code) => (
              <button /* design-system: ALLOW legacy-todo */
                key={code}
                type="button"
                onClick={() => setLang(code)}
                data-testid={`legal-lang-${code}`}
                className={`px-2 py-0.5 rounded font-semibold uppercase transition-colors ${
                  lang === code
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Article */}
      <main className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
        {eyebrow && (
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">
            <span className="h-px w-8 bg-emerald-700/40" />
            {eyebrow}
          </span>
        )}
        <h1 className="font-serif-display mt-4 text-4xl lg:text-5xl font-medium leading-tight text-slate-900">
          {title}
        </h1>
        {lastUpdated && (
          <p className="mt-3 text-xs text-slate-500 font-mono">
            {t.last_updated}: {lastUpdated}
          </p>
        )}

        <article className="lumfi-legal-prose max-w-none mt-10 text-base text-slate-700 leading-relaxed space-y-6">
          {children}
        </article>

        <div className="mt-16 pt-8 border-t border-slate-200 flex flex-wrap items-center gap-4 text-sm">
          <Link to="/" className="text-slate-600 hover:text-emerald-700 transition-colors" data-testid="legal-footer-home">
            ← {t.home}
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/privacidad" className="text-slate-600 hover:text-emerald-700 transition-colors">
            {t.privacy}
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/terminos" className="text-slate-600 hover:text-emerald-700 transition-colors">
            {t.terms}
          </Link>
        </div>
      </main>

      <footer className="border-t border-cream-300 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-8 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Lumfi · {t.rights}</span>
          <span>{t.tagline}</span>
        </div>
      </footer>
    </div>
  );
}

/** Lightweight section helper for legal copy authoring. */
export function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`legal-section-${id}`}>
      <h2 className="font-serif-display text-2xl font-medium text-slate-900 mt-12 mb-4">{title}</h2>
      <div className="space-y-3 [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 [&_a]:text-emerald-700 [&_a]:underline [&_a:hover]:text-emerald-800 [&_strong]:text-slate-900 [&_strong]:font-semibold [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px]">
        {children}
      </div>
    </section>
  );
}
